/**
 * SyncContractorsModal — F18 T18.4 paczka 3.
 *
 * Modal dwuetapowy:
 *  1. **Wybór trybu** — dropdown 2 opcje:
 *     - „Synchronizuj wszystko" (mode='full') — paginowany pełen sync z Symfonii
 *     - „Synchronizuj od daty" (mode='incremental') + date picker → IncrementalSync?dateFrom=
 *  2. **Progress** — po wysłaniu jobu, pokazuje React Query polling status + stats
 *     `{added, updated, skipped, gus_failures}` z `result.stats`.
 *
 * Wzorzec wzięty z TestConnectionModal (T18.3.x.5) + dorzucone preflight stage.
 *
 * Timeout UI 5 min (sync może trwać kilka minut dla 500 kontrahentów z GUS enrichment).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

import { isJobInFlight } from './job-in-flight';
import { type SyncRecordDetail, SyncResultPanel, type SyncResultTile } from './SyncResultPanel';
import type { JobDto } from './TestConnectionModal';

type Stage = 'choose' | 'progress';
type Mode = 'full' | 'incremental';

interface Props {
  open: boolean;
  integrationId: string | null;
  integrationName: string;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60_000; // 5 min — sync 500 kontrahentów z GUS może trwać

interface SyncStats {
  added?: number;
  updated?: number;
  unchanged?: number;
  skipped?: number;
  inactive?: number;
  excluded?: number;
  gus_failures?: number;
  with_addresses?: number;
  with_bank_accounts?: number;
  with_contacts?: number;
}

interface SyncChange {
  id?: string;
  record: string;
  fields: Array<{ field: string; from: string; to: string }>;
}

interface SyncResultPayload {
  outcome?: 'ok' | 'partial' | 'no_op';
  stats?: SyncStats;
  warnings?: string[];
  /** Realne zmiany per rekord (import diff) — sekcja „Zmiany (N)". Struktura, nie string. */
  changes?: SyncChange[];
  /** Listy rekordów per kubełek (drill-down w kafelkach). */
  details?: Record<string, SyncRecordDetail[]>;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateMinus30ISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

export function SyncContractorsModal({ open, integrationId, integrationName, onClose }: Props) {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>('choose');
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState<Mode>('full');
  const [dateFrom, setDateFrom] = useState<string>(dateMinus30ISO());
  // Weryfikacja danych w GUS podczas importu — opt-in, domyślnie wyłączone.
  const [verifyRegistries, setVerifyRegistries] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset state przy zamknięciu — wpływa na ponowne otwarcie modal-a
      setStage('choose');
      setExpanded(false);
      setMode('full');
      setVerifyRegistries(false);
      setJobId(null);
      setTimedOut(false);
      return;
    }
    if (stage !== 'progress' || !jobId) return;
    const timer = window.setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, stage, jobId]);

  // Anuluj timeout gdy job jest w stanie terminalnym (completed/failed) — bez tego
  // UI mogłoby pokazać JEDNOCZEŚNIE „Timeout" + „Sukces" gdy polling skończył a timer
  // wciąż czeka. Race wykryty w smoke teście paczki 3.

  const startSyncMutation = useMutation({
    mutationFn: async () => {
      if (!integrationId) throw new Error('No integrationId');
      const body: { mode: Mode; dateFrom?: string; verifyRegistries?: boolean } = {
        mode,
        verifyRegistries,
      };
      if (mode === 'incremental') body.dateFrom = dateFrom;
      const res = await api.post<{ jobId: string }>(
        `/erp-integrations/${integrationId}/sync-contractors`,
        body,
      );
      return res.data.jobId;
    },
    onSuccess: (id) => {
      setJobId(id);
      setStage('progress');
    },
  });

  const jobQ = useQuery({
    queryKey: ['erp-sync-job', jobId],
    queryFn: async () => {
      const res = await api.get<{ job: JobDto }>(`/erp-sync-jobs/${jobId}`);
      return res.data.job;
    },
    enabled: !!jobId && open && !timedOut && stage === 'progress',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
  });

  if (!open) return null;

  const job = jobQ.data ?? null;
  const result = (job?.result ?? null) as SyncResultPayload | null;

  const P = 'tenant.settings.integrations.syncContractors.stats';
  const d = result?.details ?? {};
  const s = result?.stats ?? {};
  const resultTiles: SyncResultTile[] = [
    {
      key: 'added',
      label: t(`${P}.added`),
      count: s.added ?? 0,
      tone: 'added',
      records: d.added ?? [],
    },
    {
      key: 'updated',
      label: t(`${P}.updated`),
      count: s.updated ?? 0,
      tone: 'updated',
      records: d.updated ?? [],
    },
    {
      key: 'unchanged',
      label: t(`${P}.unchanged`),
      count: s.unchanged ?? 0,
      tone: 'muted',
      records: d.unchanged ?? [],
    },
    {
      key: 'skipped',
      label: t(`${P}.skipped`),
      count: s.skipped ?? 0,
      tone: 'warning',
      records: d.skipped ?? [],
    },
    {
      key: 'inactive',
      label: t(`${P}.inactive`),
      count: s.inactive ?? 0,
      tone: 'muted',
      records: d.inactive ?? [],
    },
    {
      key: 'excluded',
      label: t(`${P}.excluded`),
      count: s.excluded ?? 0,
      tone: 'muted',
      records: d.excluded ?? [],
    },
    {
      key: 'gusFailures',
      label: t(`${P}.gusFailures`),
      count: s.gus_failures ?? 0,
      tone: 'danger',
      records: d.gusFailures ?? [],
    },
  ];

  // Wysyłanie żądania albo zadanie w toku — tło i Esc nie zamykają okna (X i „Zamknij" działają).
  const running =
    startSyncMutation.isPending ||
    (stage === 'progress' && isJobInFlight(job, timedOut, jobQ.isError));

  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenant.settings.integrations.syncContractors.title', { name: integrationName })}
      size="lg"
      expandable
      expanded={expanded}
      onExpandedChange={setExpanded}
      testId="sync-contractors-modal"
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      footer={
        <>
          {stage === 'choose' && (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => startSyncMutation.mutate()}
                disabled={startSyncMutation.isPending || (mode === 'incremental' && !dateFrom)}
                data-testid="sync-contractors-start-btn"
              >
                {startSyncMutation.isPending
                  ? t('tenant.settings.integrations.syncContractors.starting')
                  : t('tenant.settings.integrations.syncContractors.startButton')}
              </Button>
            </>
          )}
          {stage === 'progress' && (
            <Button variant="primary" onClick={onClose} data-testid="sync-contractors-close-btn">
              {t('common.close')}
            </Button>
          )}
        </>
      }
    >
      {stage === 'choose' && (
        <div className="space-y-4" data-testid="sync-contractors-stage-choose">
          <p className="text-sm text-text-secondary">
            {t('tenant.settings.integrations.syncContractors.chooseHint')}
          </p>
          <fieldset className="space-y-2">
            <legend className="sr-only">
              {t('tenant.settings.integrations.syncContractors.modeLegend')}
            </legend>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border-primary p-3 hover:bg-bg-secondary">
              <input
                type="radio"
                name="sync-mode"
                value="full"
                checked={mode === 'full'}
                onChange={() => setMode('full')}
                className="mt-1"
                data-testid="sync-mode-full"
              />
              <div>
                <div className="font-medium text-text-primary">
                  {t('tenant.settings.integrations.syncContractors.modeFullTitle')}
                </div>
                <div className="text-xs text-text-tertiary">
                  {t('tenant.settings.integrations.syncContractors.modeFullHint')}
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border-primary p-3 hover:bg-bg-secondary">
              <input
                type="radio"
                name="sync-mode"
                value="incremental"
                checked={mode === 'incremental'}
                onChange={() => setMode('incremental')}
                className="mt-1"
                data-testid="sync-mode-incremental"
              />
              <div className="flex-1">
                <div className="font-medium text-text-primary">
                  {t('tenant.settings.integrations.syncContractors.modeIncrementalTitle')}
                </div>
                <div className="text-xs text-text-tertiary">
                  {t('tenant.settings.integrations.syncContractors.modeIncrementalHint')}
                </div>
                {mode === 'incremental' && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-text-secondary">
                      {t('tenant.settings.integrations.syncContractors.dateFromLabel')}
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      max={todayISO()}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="mt-1 w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
                      data-testid="sync-date-from"
                    />
                  </div>
                )}
              </div>
            </label>
          </fieldset>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border-primary p-3 hover:bg-bg-secondary">
            <Checkbox
              checked={verifyRegistries}
              onChange={(e) => setVerifyRegistries(e.target.checked)}
              className="mt-0.5"
              data-testid="sync-verify-registries"
            />
            <div>
              <div className="font-medium text-text-primary">
                {t('tenant.settings.integrations.syncContractors.verifyRegistriesTitle')}
              </div>
              <div className="text-xs text-text-tertiary">
                {t('tenant.settings.integrations.syncContractors.verifyRegistriesHint')}
              </div>
            </div>
          </label>

          {startSyncMutation.isError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {t('tenant.settings.integrations.syncContractors.startError')}
            </div>
          )}
        </div>
      )}

      {stage === 'progress' && (
        <div
          className="flex h-full min-h-0 flex-col gap-4"
          data-testid={`sync-contractors-status-${
            job?.status === 'completed' || job?.status === 'failed'
              ? job.status
              : timedOut
                ? 'timeout'
                : (job?.status ?? 'pending')
          }`}
        >
          {!timedOut && (!job || job.status === 'pending' || job.status === 'running') && (
            <div className="flex items-center gap-3 rounded-md bg-bg-secondary p-3">
              <Loader2 className="h-5 w-5 animate-spin text-accent-500" aria-hidden="true" />
              <div>
                <div className="font-medium text-text-primary">
                  {job?.status === 'running'
                    ? t('tenant.settings.integrations.syncContractors.running')
                    : t('tenant.settings.integrations.syncContractors.waitingForAgent')}
                </div>
                <div className="text-xs text-text-tertiary">
                  {t('tenant.settings.integrations.syncContractors.runningHint')}
                </div>
              </div>
            </div>
          )}

          {timedOut && job?.status !== 'completed' && job?.status !== 'failed' && (
            <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
              <Clock className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
              <div>
                <div className="font-medium text-red-600 dark:text-red-400">
                  {t('tenant.settings.integrations.syncContractors.timeoutTitle')}
                </div>
                <div className="text-xs text-text-secondary">
                  {t('tenant.settings.integrations.syncContractors.timeoutHint')}
                </div>
              </div>
            </div>
          )}

          {job?.status === 'completed' && (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <div className="flex shrink-0 items-start gap-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <div className="font-medium text-green-700 dark:text-green-400">
                    {t('tenant.settings.integrations.syncContractors.successTitle')}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {t('tenant.settings.integrations.syncContractors.successHint')}
                  </div>
                </div>
              </div>

              <SyncResultPanel
                tiles={resultTiles}
                onNavigate={onClose}
                linkBase="/contractors"
                testId="import-result"
                fillHeight={expanded}
                listHeightClass="h-72"
              />
            </div>
          )}

          {job?.status === 'failed' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
                <AlertCircle
                  className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400"
                  aria-hidden="true"
                />
                <div>
                  <div className="font-medium text-red-600 dark:text-red-400">
                    {t('tenant.settings.integrations.syncContractors.failedTitle')}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {t('tenant.settings.integrations.syncContractors.failedHint')}
                  </div>
                </div>
              </div>

              {job.result && (
                <pre className="max-h-64 overflow-auto rounded-md border border-red-500/30 bg-red-500/5 p-3 font-mono text-xs text-red-700 dark:text-red-400">
                  {JSON.stringify(job.result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
